<script setup lang="ts">
	import { onBeforeUnmount, onMounted, shallowRef, watch } from "vue";

	const route = useRoute();
	const isMenuOpen = shallowRef(false);
	const previousBodyOverflow = shallowRef<string | null>(null);

	const openMenu = () => {
		isMenuOpen.value = true;
	};

	const closeMenu = () => {
		isMenuOpen.value = false;
	};

	const setBodyScrollLock = (isLocked: boolean) => {
		if (!import.meta.client) return;

		const { body } = document;

		if (isLocked) {
			previousBodyOverflow.value = body.style.overflow;
			body.style.overflow = "hidden";

			return;
		}

		if (previousBodyOverflow.value !== null) {
			body.style.overflow = previousBodyOverflow.value;
			previousBodyOverflow.value = null;
		}
	};

	const handleKeydown = (event: KeyboardEvent) => {
		if (event.key === "Escape") closeMenu();
	};

	watch(isMenuOpen, setBodyScrollLock);
	watch(() => route.fullPath, closeMenu);

	onMounted(() => {
		window.addEventListener("keydown", handleKeydown);
	});

	onBeforeUnmount(() => {
		window.removeEventListener("keydown", handleKeydown);
		setBodyScrollLock(false);
	});
</script>

<template>
	<div
		class="layout-default container container--wide"
		@keydown.esc="closeMenu">
		<header class="layout-default__mobile-header">
			<img
				class="layout-default__mobile-logo"
				src="@/assets/images/logo.png"
				alt="RexRent" />

			<Button
				class="layout-default__menu-trigger"
				icon="pi pi-bars"
				type="button"
				aria-label="Открыть меню"
				aria-controls="lk-mobile-menu"
				:aria-expanded="isMenuOpen"
				@click="openMenu" />
		</header>

		<AsideMenu
			mobile-responsive
			:mobile-open="isMenuOpen"
			@close="closeMenu" />

		<button
			v-if="isMenuOpen"
			class="layout-default__menu-backdrop"
			type="button"
			aria-label="Закрыть меню"
			@click="closeMenu" />

		<main class="layout-default__content">
			<slot />
		</main>
	</div>
</template>

<style scoped lang="scss">
	@import "@/assets/scss/layouts/default";
</style>
